/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import {
  Price,
  Icon,
  Card,
  Accordion,
  AccordionSection,
} from '@adobe-commerce/elsie/components';
import {
  ChevronDown,
  ChevronUp,
  Gift,
  CheckWithCircle,
} from '@adobe-commerce/elsie/icons';
import { useText } from '@adobe-commerce/elsie/i18n';
import {
  GiftFormDataType,
  GiftWrappingConfigProps,
  GiftOptionsReadOnlyViewProps,
  GiftOptionsViewProps,
} from '@/cart/types';

export interface ReadOnlyFormViewProps {
  view: GiftOptionsViewProps;
  giftOptions: GiftFormDataType;
  readOnlyFormOrderView: GiftOptionsReadOnlyViewProps;
  giftWrappingConfig: GiftWrappingConfigProps[] | [];
}

export const ReadOnlyFormView: FunctionComponent<ReadOnlyFormViewProps> = ({
  view,
  giftOptions,
  giftWrappingConfig,
  readOnlyFormOrderView,
}) => {
  const dictionary = useText({
    readOnlyProductTitle: 'Cart.GiftOptions.product.readOnlyFormView.title',
    wrapping: 'Cart.GiftOptions.product.readOnlyFormView.wrapping',
    recipient: 'Cart.GiftOptions.product.readOnlyFormView.recipient',
    sender: 'Cart.GiftOptions.product.readOnlyFormView.sender',
    message: 'Cart.GiftOptions.product.readOnlyFormView.message',

    readOnlyOrderTitle: 'Cart.GiftOptions.order.readOnlyFormView.title',
    readOnlyOrderGiftReceiptTitle:
      'Cart.GiftOptions.order.readOnlyFormView.giftReceipt',
    readOnlyOrderGiftReceiptText:
      'Cart.GiftOptions.order.readOnlyFormView.giftReceiptText',
    readOnlyOrderGiftPrintCardTitle:
      'Cart.GiftOptions.order.readOnlyFormView.printCard',
    readOnlyOrderGiftPrintCardText:
      'Cart.GiftOptions.order.readOnlyFormView.printCardText',
    readOnlyOrderGiftWrapTitle:
      'Cart.GiftOptions.order.readOnlyFormView.giftWrap',
    readOnlyOrderGiftWrapOptionsText:
      'Cart.GiftOptions.order.readOnlyFormView.giftWrapOptions',

    readOnlyOrderFormTitle: 'Cart.GiftOptions.order.readOnlyFormView.formTitle',
    readOnlyOrderFormTo: 'Cart.GiftOptions.order.readOnlyFormView.formTo',
    readOnlyOrderFormFrom: 'Cart.GiftOptions.order.readOnlyFormView.formFrom',
    readOnlyOrderFormMessageTitle:
      'Cart.GiftOptions.order.readOnlyFormView.formMessageTitle',
  });

  const {
    recipientName,
    senderName,
    message,
    giftReceiptIncluded,
    printedCardIncluded,
    isGiftWrappingSelected,
  } = giftOptions;

  const giftWrapCart = giftWrappingConfig?.find(
    ({ uid }) => uid === giftOptions?.giftWrappingId
  );
  const isGiftMessageProvided = !!recipientName || !!senderName || !!message;
  const giftWrapProductSelectLabel = giftWrapCart?.design;
  const shouldRenderGiftOptions =
    giftReceiptIncluded || printedCardIncluded || giftWrapCart?.selected;

  const productGiftOptionsConfig = [
    {
      id: 1,
      title: dictionary.wrapping,
      message: isGiftWrappingSelected ? giftWrapProductSelectLabel : '',
    },
    {
      id: 2,
      title: dictionary.recipient,
      message: recipientName,
    },
    {
      id: 3,
      title: dictionary.sender,
      message: senderName,
    },
    {
      id: 4,
      title: dictionary.message,
      message,
    },
  ];

  const noProductGiftOptionsApplied = productGiftOptionsConfig.every(
    ({ message }) => !message
  );

  if (view === 'product' && !noProductGiftOptionsApplied) {
    return (
      <Accordion
        data-testid="gift-options-product"
        iconClose={ChevronUp}
        iconOpen={ChevronDown}
        actionIconPosition="right"
      >
        <AccordionSection
          title={dictionary.readOnlyProductTitle}
          showIconLeft={true}
          iconLeft={Gift}
          defaultOpen={false}
          renderContentWhenClosed={false}
        >
          <div>
            {productGiftOptionsConfig
              .filter((element) => element.message)
              .map((config) => {
                return (
                  <p key={config.id}>
                    {config.title} {config.message}
                  </p>
                );
              })}
          </div>
        </AccordionSection>
      </Accordion>
    );
  }

  if (view === 'order' && (isGiftMessageProvided || shouldRenderGiftOptions)) {
    const renderHeader = (
      <div className="cart-gift-options-readonly__header">
        <Icon source={Gift} size="24" />
        <span>{dictionary.readOnlyOrderTitle}</span>
      </div>
    );

    const selectedGiftOptions = (
      <>
        {giftReceiptIncluded ? (
          <div className="cart-gift-options-readonly__checkboxes cart-gift-options-readonly__checkboxes--gift-receipt">
            <Icon source={CheckWithCircle} size="16" />
            <p>{dictionary.readOnlyOrderGiftReceiptTitle}</p>
            <p>{dictionary.readOnlyOrderGiftReceiptText}</p>
          </div>
        ) : null}
        {printedCardIncluded ? (
          <div className="cart-gift-options-readonly__checkboxes cart-gift-options-readonly__checkboxes--print-card">
            <Icon source={CheckWithCircle} size="16" />
            <p>{dictionary.readOnlyOrderGiftPrintCardTitle}</p>
            <p>{dictionary.readOnlyOrderGiftPrintCardText}</p>
          </div>
        ) : null}
        {giftWrapCart?.selected ? (
          <div className="cart-gift-options-readonly__checkboxes cart-gift-options-readonly__checkboxes--gift-wrap">
            <Icon source={CheckWithCircle} size="16" />
            <p>
              {dictionary.readOnlyOrderGiftWrapTitle} (+
              <Price
                amount={giftWrapCart.price.value}
                currency={giftWrapCart.price.currency}
                weight="normal"
              />
              )
            </p>
            <p>{`${dictionary.readOnlyOrderGiftWrapOptionsText} ${giftWrapCart?.design}`}</p>
          </div>
        ) : null}
      </>
    );

    const enteredGiftMessage = isGiftMessageProvided ? (
      <div className="cart-gift-options-readonly__form">
        <div>{dictionary.readOnlyOrderFormTitle}</div>
        <div>
          <p>
            <span>{dictionary.readOnlyOrderFormTo}</span>
            <span>{recipientName}</span>
          </p>
          <p>
            <span>{dictionary.readOnlyOrderFormFrom}</span>
            <span>{senderName}</span>
          </p>
        </div>
        <div>
          <p>{dictionary.readOnlyOrderFormMessageTitle}</p>
          <p>{message}</p>
        </div>
      </div>
    ) : null;

    return (
      <Card variant={readOnlyFormOrderView}>
        {renderHeader}
        {selectedGiftOptions}
        {enteredGiftMessage}
      </Card>
    );
  }

  return null;
};
